import { ICompanyModel } from '../models';

import { addMonths, setMonthDateWithoutOverflow, subtractMonths } from '../../shared/helpers/abacusTimeFormatter';

import { AMEX_BILLING_STRIPE_PLAN_TYPE } from '../constants';

export function checkIfBillingPlanIsAmex(billingPlan: string) {
  return ([AMEX_BILLING_STRIPE_PLAN_TYPE.FREE_PLAN, AMEX_BILLING_STRIPE_PLAN_TYPE.PRO_PLAN] as string[]).includes(billingPlan);
}

export function getLatestAndNextBillingDates(company: ICompanyModel) {
  let latestBillingDate: Date, nextBillingDate: Date;

  const currentMonth = new Date().getMonth();
  const prevMonth = subtractMonths(new Date(), 1).getMonth();
  const nextMonth = addMonths(new Date(), 1).getMonth();
  const billingDay = company.billing_day;

  if (new Date().getDate() > billingDay) {
    latestBillingDate = setMonthDateWithoutOverflow(new Date(new Date().getFullYear(), currentMonth), billingDay);
    nextBillingDate = setMonthDateWithoutOverflow(new Date(new Date().getFullYear(), nextMonth), billingDay);
  } else {
    latestBillingDate = setMonthDateWithoutOverflow(new Date(new Date().getFullYear(), prevMonth), billingDay);
    nextBillingDate = setMonthDateWithoutOverflow(new Date(new Date().getFullYear(), currentMonth), billingDay);
  }

  return { latestBillingDate, nextBillingDate };
}

interface MileageRateEntry {
  startDate: string;
  rate: number;
}

export function getMileageRateFromConfig(mileage_rate_per_year: ICompanyModel['mileage_rate_per_year'], expenseDate?: number) {
  if (mileage_rate_per_year.hasOwnProperty('custom')) {
    return mileage_rate_per_year.custom;
  }

  const date = new Date(expenseDate ?? Date.now());
  const rates: MileageRateEntry[] = mileage_rate_per_year;

  const matchingRate = rates.filter((entry) => new Date(entry.startDate) <= date).pop();

  return matchingRate ? matchingRate.rate : rates[0].rate;
}

/**
 * Maps currency codes to their distance unit and standard mileage rate type.
 * Only currencies with a known standard rate are listed; all others default to miles.
 */
export const STANDARD_RATE_BY_CURRENCY: Record<string, { unit: 'miles' | 'km'; rateType: 'irs' | 'ato' }> = {
  USD: { unit: 'miles', rateType: 'irs' },
  AUD: { unit: 'km', rateType: 'ato' }
};

/**
 * Returns the distance unit for the given currency code.
 * Defaults to 'miles' for unknown currencies.
 */
export function getDistanceUnitForCurrency(currencyCode: string): 'miles' | 'km' {
  return STANDARD_RATE_BY_CURRENCY[currencyCode]?.unit ?? 'miles';
}

/**
 * Returns the standard mileage rate type for the given currency code,
 * or null if no standard rate applies.
 */
export function getStandardRateType(currencyCode: string): 'irs' | 'ato' | null {
  return STANDARD_RATE_BY_CURRENCY[currencyCode]?.rateType ?? null;
}
